import {
  createContext,
  createSignal,
  useContext,
  type Accessor,
  type ParentComponent,
} from 'solid-js'
import ru from './locales/ru'
import en from './locales/en'

const dictionaries = { ru, en }

export type Locale = keyof typeof dictionaries
export type Dictionary = typeof ru

const STORAGE_KEY = 'ritmflow.locale'

function detectInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'ru' || stored === 'en') return stored
  } catch {
    /* localStorage недоступен (SSR / private mode) — игнорируем. */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language?.toLowerCase() ?? '' : ''
  return nav.startsWith('en') ? 'en' : 'ru'
}

// Модульная переменная — синхронный «слепок» текущей локали для кода вне
// Solid-контекста (например, fetch-обёртка `apiRequest`, которая не может
// вызывать `useI18n`). Обновляется в `setLocale` провайдера.
let currentLocale: Locale = detectInitialLocale()

/** Возвращает текущую локаль без подписки на реактивность — для использования
 *  из не-Solid модулей (HTTP-слой, общие хелперы). */
export function getCurrentLocale(): Locale {
  return currentLocale
}

// Доступ по dot-path — возвращает сам ключ, если значения нет (чтобы пропуск
// перевода сразу было видно в UI).
function getByPath(obj: unknown, path: string): string {
  const parts = path.split('.')
  let cur: unknown = obj
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[p]
    } else {
      return path
    }
  }
  return typeof cur === 'string' ? cur : path
}

function applyParams(template: string, params?: Record<string, string | number>): string {
  if (!params) return template
  let out = template
  for (const [k, v] of Object.entries(params)) {
    out = out.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v))
  }
  return out
}

type I18nContextValue = {
  locale: Accessor<Locale>
  setLocale: (l: Locale) => void
  t: (key: string, params?: Record<string, string | number>) => string
}

const I18nContext = createContext<I18nContextValue>()

export const I18nProvider: ParentComponent = (props) => {
  const [locale, setLocaleSignal] = createSignal<Locale>(currentLocale)

  // При первом монтировании выставляем lang на <html> — это даст браузеру
  // правильную локализацию автогенерируемых сообщений (например, валидация
  // встроенных input'ов).
  try {
    document.documentElement.lang = locale()
  } catch {
    /* SSR — пропускаем. */
  }

  const setLocale = (l: Locale) => {
    currentLocale = l
    setLocaleSignal(l)
    try {
      localStorage.setItem(STORAGE_KEY, l)
    } catch {
      /* private mode — переключение всё равно сработает на текущую сессию. */
    }
    try {
      document.documentElement.lang = l
    } catch {
      /* SSR — пропускаем. */
    }
  }

  const t = (key: string, params?: Record<string, string | number>) => {
    const tpl = getByPath(dictionaries[locale()], key)
    return applyParams(tpl, params)
  }

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>{props.children}</I18nContext.Provider>
  )
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    throw new Error('useI18n must be used inside <I18nProvider>')
  }
  return ctx
}
